import {
  Box,
  Divider,
  Flex,
  Image,
  Text,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  IconButton,
  Icon,
} from '@chakra-ui/react';
import useBrokerLogo from 'hooks/modules/brokerAccount/useBrokerLogo';
import useCurrencyText from 'hooks/shared/useCurrencyText';

import {
  PortfolioAccountsData,
  PortfolioAccountItem,
} from 'model/modules/portfolio/accounts';
import { BrokerType } from 'enums/shared/brokerType';
import ProfitText from 'presentation/components/shared/amount/ProfitText';
import AppCard from 'presentation/components/shared/card/AppCard';
import { Color } from 'presentation/theme/colorScheme';
import { FontSize } from 'presentation/theme/fontSizes';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AppRoute from 'router/AppRoute';
import addAccountIcon from 'assets/img/icons/nav/connect-account-3d.png';
import classNames from 'classnames';
import { useUpdateAccountMutation } from 'rtkQuery/sharespro/queries/home';
import { ReactComponent as MyCustomIcon } from 'assets/img/icons/edit-icon.svg';
import TextInput from 'presentation/components/shared/fields/TextInput';
import { ButtonVariant } from 'presentation/theme/components/button';

interface Props extends PortfolioAccountsData {
  onTotalClick: () => void;
  refetch: () => void;
  onAccountClick: (accountId: ID) => void;
  activeAccountId?: ID;
  showSelectedAccount: boolean;
}

const customIconBrokerTypes = [
  BrokerType.PE,
  BrokerType.Crypto,
  BrokerType.Arbitr,
  BrokerType.CryptoDeposit,
  BrokerType.IPO,
  BrokerType.PrimeIPO,
];

const PortfolioAccountList: FC<Props> = ({
  total,
  items,
  onAccountClick,
  onTotalClick,
  activeAccountId,
  showSelectedAccount,
  refetch,
}) => {
  const currencyFormatter = useCurrencyText();
  const brokerLogo = useBrokerLogo();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [updateAccount] = useUpdateAccountMutation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedAccount, setSelectedAccount] =
    useState<PortfolioAccountItem | null>(null);
  const [newAccountName, setNewAccountName] = useState('');

  const handleEditClick = (account: PortfolioAccountItem): void => {
    setSelectedAccount(account);
    setNewAccountName(account.name);
    onOpen();
  };

  const handleSave = async (): Promise<void> => {
    if (selectedAccount) {
      await updateAccount({
        account_id: selectedAccount.id,
        name: newAccountName,
        description: '',
      });
      onClose();
      refetch();
    }
  };

  return (
    <AppCard overflow="hidden">
      <Box
        onClick={onTotalClick}
        cursor="pointer"
        className={classNames('menuListItem', {
          menuListItem__selected:
            showSelectedAccount && activeAccountId === undefined,
        })}
        bgColor={
          showSelectedAccount && activeAccountId === undefined
            ? Color.Shade200
            : Color.Surface
        }
        _hover={{ bgColor: Color.Shade200 }}
        pt="20px"
        px="25"
      >
        <Text fontWeight={600} fontSize={FontSize.lg}>
          {currencyFormatter(total.amount, { symbol: total.currency.symbol })}
        </Text>
        <ProfitText
          profitAmount={total.balanceChange}
          profitPercent={total.balanceChangePercent}
          currency={total.currency}
          mb="20px"
        />
        <Divider />
      </Box>
      {items.map((account) => {
        const isSelected =
          showSelectedAccount && activeAccountId === account.id;
        const accountLogo = brokerLogo({
          brokerType: account.brokerType,
          accountName: account.name,
        });
        const isCustomIcon =
          customIconBrokerTypes.includes(account.brokerType) ||
          (accountLogo.includes('.png') &&
            account.brokerType !== BrokerType.Tinkoff);

        return (
          <Box
            key={account.id}
            className={classNames('menuListItem', {
              menuListItem__selected: isSelected,
            })}
            px="20px"
            cursor="pointer"
            bgColor={isSelected ? Color.Shade200 : Color.Surface}
            _hover={{ bgColor: Color.Shade200 }}
          >
            <Flex
              display="flex"
              alignItems="center"
              py="10px"
              px="5px"
              justifyContent="space-between"
            >
              <Flex alignItems="center">
                <Flex
                  w="30px"
                  h="30px"
                  mr="15px"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Image
                    w={isCustomIcon ? '52px' : '30px'}
                    h={isCustomIcon ? '52px' : '30px'}
                    maxW="none"
                    mixBlendMode={isCustomIcon ? 'multiply' : 'normal'}
                    src={accountLogo}
                  />
                </Flex>
                <Box onClick={() => onAccountClick(account.id)}>
                  <Text mb="5px" fontSize={FontSize.xs}>
                    {account.name}
                  </Text>
                  <Text mb="5px" fontWeight={700}>
                    {currencyFormatter(account.balance, {
                      symbol: total.currency.symbol,
                    })}
                  </Text>
                  <ProfitText
                    profitAmount={account.balanceChange}
                    profitPercent={account.balanceChangePercent}
                    currency={account.currency}
                    fontSize={FontSize.xs}
                  />
                </Box>
              </Flex>
              <IconButton
                size="m"
                variant={ButtonVariant.Light}
                onClick={() => handleEditClick(account)}
                aria-label="Edit"
                icon={<Icon as={MyCustomIcon} />}
              ></IconButton>
            </Flex>
            <Divider />
          </Box>
        );
      })}
      <Flex
        py="18px"
        px="25px"
        onClick={() => navigate(AppRoute.createBrokerAccount)}
        cursor="pointer"
        _hover={{ bgColor: Color.Shade200 }}
        alignItems="center"
      >
        <Image src={addAccountIcon} w="52px" h="52px" mr="12px" />
        <Text fontWeight={600}>{t('connectBrokerAccount')}</Text>
      </Flex>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('editAccountName')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <TextInput
              value={newAccountName}
              onChange={(e) => setNewAccountName(e.target.value)}
              maxLength={20}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={() => {
                handleSave();
              }}
            >
              {t('save')}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              {t('cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AppCard>
  );
};

export default PortfolioAccountList;
